import { useMemo } from 'react';
import parse from 'html-react-parser';
import EmbeddedPassword from './EmbeddedPassword';

/**
 * ArticleContent Component
 * Renders article HTML content with embedded password support
 * Parses [[password:uuid:name]] markers and replaces with interactive components
 */
export default function ArticleContent({ content, className = '' }) {
  // Parse and transform the content
  const transformedContent = useMemo(() => {
    if (!content) return <p>No content available.</p>;

    // First, transform password markers in text
    // This regex finds [[password:uuid:name]] patterns
    const passwordRegex = /\[\[password:([a-f0-9-]+)(?::([^\]]+))?\]\]/gi;

    // Replace password markers with a special HTML element that we can identify
    const contentWithMarkers = content.replace(passwordRegex, (match, id, name) => {
      return `<embedded-password data-id="${id}" data-name="${name || ''}"></embedded-password>`;
    });

    // Parse HTML and replace embedded-password elements with React components
    const options = {
      replace: (domNode) => {
        if (domNode.name === 'embedded-password') {
          const id = domNode.attribs['data-id'];
          const name = domNode.attribs['data-name'] || null;
          return <EmbeddedPassword key={id} passwordId={id} name={name} />;
        }
      },
    };

    try {
      return parse(contentWithMarkers, options);
    } catch (err) {
      console.error('Failed to parse article content:', err);
      return <p>Error rendering content.</p>;
    }
  }, [content]);

  return (
    <div className={`prose prose-neutral dark:prose-invert max-w-none ${className}`}>
      {transformedContent}
    </div>
  );
}
